<template>
  <div class="philosophy-page">
    <div class="ambient-glow"></div>

    <div class="container">
      <section class="hero-section v-scroll-reveal">
        <div class="brand-tag">SINCE 2026</div>
        <h1 class="main-title">
          <span class="gradient-text">来秋社区</span>
          <span class="dot">.</span>
        </h1>
        <p class="hero-en">LaiQiu Network.</p>
      </section>

      <section class="section core-concept v-scroll-reveal">
        <h2 class="section-label">Core Philosophy</h2>
        <div class="content-box">
          <p class="large-text">
            藏在秋风里的社区，留给认真生活的人。
            我们以技术为桥，以热爱为灯，<span class="highlight">让交流有温度，让成长有方向</span>。
          </p>
        </div>
      </section>

      <section class="section v-scroll-reveal">
        <div class="mission-grid">
          <div class="grid-item">
            <h3 class="item-title">使命 / MISSION</h3>
            <p class="item-desc">打造技术与生活共生的社区，让知识可分享，让同好可相遇。</p>
          </div>
          <div class="grid-item">
            <h3 class="item-title">愿景 / VISION</h3>
            <p class="item-desc">做有温度、有深度的数字家园，让每一份热爱都有归宿。</p>
          </div>
        </div>
      </section>

      <section class="section v-scroll-reveal">
        <h2 class="section-label">Values & Spirit</h2>
        <div class="values-flex">
          <div class="value-node">
            <span class="node-num">01</span>
            <h4>专注技术</h4>
            <p>保持专业与真诚，用内容沉淀价值。</p>
          </div>
          <div class="value-node">
            <span class="node-num">02</span>
            <h4>忠于热爱</h4>
            <p>不迎合浮躁，只守护初心与热爱。</p>
          </div>
          <div class="value-node">
            <span class="node-num">03</span>
            <h4>温暖包容</h4>
            <p>友善相待，尊重每一种表达。</p>
          </div>
        </div>
      </section>

      <section class="section design-philosophy v-scroll-reveal">
        <div class="aesthetic-card">
          <div class="card-inner">
            <h2 class="section-label">Design Aesthetic</h2>
            <p class="aesthetic-text">
              我们坚持<strong>简洁流畅、真实易用</strong>。
              用现代技术打造舒适体验，用干净氛围守护社区本真。
              不止技术，更是人生。
            </p>
          </div>
        </div>
      </section>

      <section class="section footer-quote v-scroll-reveal">
        <div class="end-line"></div>
        <p class="quote-text">来秋相遇，不负热爱。</p>
        <p class="en-quote">Meet in LaiQiu, live up to your love.</p>
        <div class="scroll-top-hint" @click="scrollToTop">BACK TO TOP</div>
      </section>
    </div>
  </div>
</template>

<script setup>
import { onMounted } from 'vue';

const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

onMounted(() => {
  const observerOptions = {
    threshold: 0.1,
    rootMargin: '0px 0px -100px 0px'
  };

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('active');
      }
    });
  }, observerOptions);

  document.querySelectorAll('.v-scroll-reveal').forEach(el => {
    observer.observe(el);
  });
});
</script>

<style scoped lang="scss">
// 核心配色变量
$black-deep: #050505;
$black-surface: #0a0a0a;
$gray-text: #86868b;
$white-pure: #ffffff;
$accent-gold: #c9a063;

.philosophy-page {
  background-color: $black-deep;
  color: $white-pure;
  font-family: "Inter", "PingFang SC", -apple-system, sans-serif;
  position: relative;
  line-height: 1.6;
  width: 100%;

  /* ✅ 彻底修复：页面自身不产生任何滚动条 */
  overflow: visible;
  height: auto;
  min-height: unset;
}

// 氛围背景
.ambient-glow {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: radial-gradient(circle at 50% 20%, rgba(201, 160, 99, 0.05) 0%, transparent 50%),
    radial-gradient(circle at 10% 80%, rgba(255, 255, 255, 0.02) 0%, transparent 40%);
  pointer-events: none;
  z-index: 0;
}

.container {
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 40px;
  position: relative;
  z-index: 1;
}

// 滚动动画基础
.v-scroll-reveal {
  opacity: 0;
  transform: translateY(40px);
  transition: all 1.2s cubic-bezier(0.215, 0.61, 0.355, 1);

  &.active {
    opacity: 1;
    transform: translateY(0);
  }
}

// Hero Section
.hero-section {
  min-height: 85vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;

  .brand-tag {
    font-size: 12px;
    letter-spacing: 4px;
    color: $accent-gold;
    margin-bottom: 20px;
    font-weight: 500;
  }

  .main-title {
    font-size: clamp(3rem, 10vw, 6rem);
    font-weight: 700;
    margin: 0;

    .gradient-text {
      background: linear-gradient(180deg, #fff 0%, #888 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }

    .dot {
      color: $accent-gold;
    }
  }

  .hero-en {
    margin-top: 24px;
    font-size: 18px;
    color: $gray-text;
    font-weight: 300;
    letter-spacing: 2px;
  }
}

// 通用标签
.section-label {
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 3px;
  color: $accent-gold;
  margin-bottom: 40px;
  display: block;
  font-weight: 600;
}

.section {
  padding: 150px 0;
}

// 核心理念内容
.large-text {
  font-size: clamp(1.5rem, 4vw, 2.5rem);
  font-weight: 500;
  line-height: 1.4;
  color: #efeff1;

  .highlight {
    color: $white-pure;
    text-decoration: underline;
    text-underline-offset: 8px;
    text-decoration-color: rgba($accent-gold, 0.4);
  }
}

// 使命愿景网格
.mission-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 100px;

  .item-title {
    font-size: 24px;
    margin-bottom: 24px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    padding-bottom: 16px;
  }

  .item-desc {
    color: $gray-text;
    font-size: 18px;
    line-height: 1.8;
  }
}

// 价值精神
.values-flex {
  display: flex;
  justify-content: space-between;
  gap: 40px;

  .value-node {
    flex: 1;

    .node-num {
      font-family: "Georgia", serif;
      font-style: italic;
      font-size: 32px;
      color: rgba($accent-gold, 0.6);
      display: block;
      margin-bottom: 15px;
    }

    h4 {
      font-size: 20px;
      margin-bottom: 12px;
    }

    p {
      color: $gray-text;
      font-size: 15px;
    }
  }
}

// 设计美学卡片
.aesthetic-card {
  background: linear-gradient(145deg, #0f0f0f, #050505);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 40px;
  padding: 80px 60px;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 2px;
    background: linear-gradient(90deg, transparent, $accent-gold, transparent);
    opacity: 0.3;
  }

  .aesthetic-text {
    font-size: 20px;
    color: #d1d1d6;
    max-width: 700px;
    line-height: 2;

    strong {
      color: $white-pure;
      font-weight: 600;
    }
  }
}

// 结尾
.footer-quote {
  text-align: center;
  padding-bottom: 200px;

  .end-line {
    width: 1px;
    height: 80px;
    background: linear-gradient(to bottom, $accent-gold, transparent);
    margin: 0 auto 60px;
  }

  .quote-text {
    font-size: 28px;
    margin-bottom: 12px;
    letter-spacing: 2px;
  }

  .en-quote {
    color: $gray-text;
    font-style: italic;
    opacity: 0.6;
  }

  .scroll-top-hint {
    margin-top: 100px;
    font-size: 12px;
    letter-spacing: 4px;
    cursor: pointer;
    transition: color 0.3s;

    &:hover {
      color: $accent-gold;
    }
  }
}

// 响应式适配
@media (max-width: 768px) {
  .container {
    padding: 0 24px;
  }

  .section {
    padding: 80px 0;
  }

  .mission-grid {
    grid-template-columns: 1fr;
    gap: 40px;
  }

  .values-flex {
    flex-direction: column;
  }

  .aesthetic-card {
    padding: 40px 24px;
  }

  .hero-section .main-title {
    font-size: 3rem;
  }
}
</style>